import { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { ingestBarcodeScan, withRouter } from "../../../shared/utility";
import { BarcodeScannerViewModel } from "../../../viewmodels/setup/barcodeScanner/BarcodeScannerViewModel";
import EmptyModal from "../../../components/modals/EmptyModal";
import AssemblyItemsListComponent from "../../../components/forms/setup/barcodeScanner/AssemblyItemsListComponent";
import * as actions from "../../../store/actions/index";

function BarcodeScanner(props) {
  const { addSnackbar, logout } = props;
  const {
    getAllAssemblyItems,
    assignAssemblyItem,
    setEmptyModelOpen,
    emptyModal,
    assemblyItems,
  } = BarcodeScannerViewModel(logout, addSnackbar);

  const [barcode, setBarcode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const handleScan = () => {
    if (!barcode.trim()) {
      setError("Please enter a barcode");
      setResult(null);
      return;
    }

    try {
      const parsed = ingestBarcodeScan(barcode);
      setResult(parsed);
      setError(null);
    } catch (err) {
      setError("Failed to parse barcode. Please check the format.");
      setResult(null);
    }
  };

  useEffect(() => {
    getAllAssemblyItems();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleScan();
    }
  };

  const handleClear = () => {
    setBarcode("");
    setResult(null);
    setError(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleOpenAssemblyItems = async () => {
    const createListComponent = () => (
      <AssemblyItemsListComponent
        assemblyItemsList={assemblyItems}
        setOpen={setEmptyModelOpen}
        sku={result.plu}
        assignAssemblyItem={assignAssemblyItem}
        restoreListComponent={() => {
          setEmptyModelOpen({
            childComponent: createListComponent(),
            open: true,
          });
        }}
      />
    );

    setEmptyModelOpen({
      childComponent: createListComponent(),
      open: true,
    });
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="border-b border-[#E2E8F0] px-6 py-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#64748B]">
              Setup Tool
            </p>
            <h1 className="mt-2 text-[1.65rem] font-semibold tracking-tight text-[#0F172A] sm:text-3xl">
              Barcode Extract Tool
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748B]">
              Paste or scan a GS1 barcode to extract supplier SKU, production
              date, net weight, and serial number.
            </p>
          </div>

          <div className="space-y-5 p-6">
            <div>
              <label
                htmlFor="barcode"
                className="block text-sm font-semibold text-[#0F172A]"
              >
                Barcode
              </label>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                <input
                  ref={inputRef}
                  type="text"
                  id="barcode"
                  name="barcode"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Scan or paste your barcode here"
                  className="min-h-[46px] flex-1 rounded-xl border border-[#E2E8F0] bg-white px-4 text-sm text-[#0F172A] shadow-sm placeholder:text-[#64748B]/70 focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleScan}
                  className="inline-flex min-h-[46px] justify-center rounded-xl border border-[#2563EB] bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:border-[#1D4ED8] hover:bg-[#1D4ED8]"
                >
                  Scan
                </button>
                {barcode && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="inline-flex min-h-[46px] justify-center rounded-xl border border-[#E2E8F0] bg-white px-5 py-2.5 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:bg-[#F6F8FB]"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm font-medium text-[#991B1B]">
                {error}
              </div>
            )}

            {result && (
              <div className="rounded-[18px] border border-[#A7F3D0] bg-[#ECFDF5]/50 p-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-[#0F172A]">
                      Parsed Barcode Information
                    </h2>
                    <p className="text-sm text-[#64748B]">
                      Review the extracted fields before assigning the supplier
                      item.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenAssemblyItems}
                    className="inline-flex justify-center rounded-xl border border-[#0D9488] bg-[#0D9488] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:border-[#0F766E] hover:bg-[#0F766E]"
                  >
                    View Supplier Items
                  </button>
                </div>

                <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    ["Supplier SKU", result.plu],
                    ["Production Date", result.date],
                    ["Net Weight", `${result.weight} kg`],
                    ["Serial Number", result.serial],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm"
                    >
                      <dt className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
                        {label}
                      </dt>
                      <dd className="mt-2 break-words font-mono text-sm font-semibold text-[#0F172A]">
                        {value || "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </section>
      </div>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(BarcodeScanner),
);
